import { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import ButtonViewGroup from "components/ButtonViewGroup/ButtonViewGroup";
import { getRFQId, updateRFXSupplierList } from "actions/RFQ/rfqsAction";
import GridViewSupplier from "./GridViewSupplier";
import ListViewSupplier from "./ListViewSupplier";
import { Input } from "components/RFQ/Input/Input";
import tw, { styled, css } from "twin.macro";
import InviteForm from "./InviteForm";
import { useField } from "formik";
import { Heading } from "components/Heading";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import { SupplierListSuffix } from "pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/Suppliers/SupplierListSuffix";
import { getSupplierListType } from "newui/features/Rfx/SupplierList/supplier-list-types";
import { isRfxOnboardingType } from "newui/features/Rfx/utils";

export const RFQSupplierStyled = styled.div(() => [
	tw`flex flex-col w-full`,
	css`
		.img-card {
			object-fit: cover;
			height: 104px;
			width: 100%;
		}
		.rfq-form-sub-title {
			font-family: Roboto;
			font-weight: 700;
			font-size: 20px;
			line-height: 30px;
			height: 30px;
		}
		.rfq-form-caption {
			font-family: Roboto;
			font-size: 14px;
			line-height: 20px;
			height: 20px;
			margin-bottom: 30px;
		}
		.layout-jumbotron {
			display: flex;
		}
		.layout-jumbotron-1 {
			flex: 1;
		}
		.layout-jumbotron-2 {
			flex: 1;
			align-self: flex-end;
		}
		.layout-jumbotron-title {
			font-family: Roboto;
			font-weight: 700;
			font-size: 20px;
			line-height: 30px;
			color: #101828;
		}
		.layout-jumbotron-subtitle {
			font-family: Roboto;
			font-weight: 400;
			font-size: 14px;
			line-height: 30px;
			color: #475467;
		}
	`,
]);

function Suppliers({ rfqId, showErrorMessage }) {
	const [view, setView] = useState("grid");
	const [localSupplierLists, setLocalSupplierLists] = useState([]);
	const [, { value: invitedSuppliers }, { setValue: setInvitedSuppliers }] =
		useField("suppliers");
	const formik = useFormikContext();
	const queryClient = useQueryClient();

	const {
		data: rfqData,
		isLoading: isLoadingRFQ,
		refetch,
	} = useQuery({
		queryKey: ["rfq", rfqId],
		queryFn: () => getRFQId(rfqId),
		enabled: !!rfqId,
		staleTime: 30000,
	});

	const supplierListUuid = rfqData?.supplierList?.uuid;

	// update local supplier lists when rfqData changes
	useEffect(() => {
		if (rfqData?.supplierList?.suppliers) {
			setLocalSupplierLists(rfqData.supplierList.suppliers);
		}
	}, [rfqData?.supplierList?.suppliers]);

	const updateSupplierListMutation = useMutation({
		mutationFn: ({ rfqId, supplierListId }) =>
			updateRFXSupplierList(rfqId, supplierListId),
		onSuccess: (response) => {
			if (response) {
				// update local state with new supplier list
				setLocalSupplierLists(response.supplierList?.suppliers || []);

				// update formik state
				const newSupplierIds =
					response.supplierList?.suppliers?.map((supplier) => supplier.id) ||
					[];
				setInvitedSuppliers(newSupplierIds);
				formik.setFieldValue("suppliers", newSupplierIds);

				// invalidate and refetch the RFQ data to get the latest state
				queryClient.invalidateQueries({ queryKey: ["rfq", rfqId] });
				refetch();
			}
		},
		onError: (error) => {
			console.error("Error updating supplier list:", error);
		},
	});

	useEffect(() => {
		if (invitedSuppliers?.length < 1)
			setInvitedSuppliers(localSupplierLists.map((supplier) => supplier.id));
		// eslint-disable-next-line
	}, [localSupplierLists]);

	const handleInviteSupplier = (supplier) => {
		setInvitedSuppliers([...invitedSuppliers, supplier]);
	};
	const handleRemoveSupplier = (supplier) => {
		setInvitedSuppliers(invitedSuppliers.filter((sup) => sup !== supplier));
	};

	const getSupplierListOptionsByProject = () => {
		const isOnboardingRfx = isRfxOnboardingType(rfqData.rfxType);
		const options =
			rfqData?.project?.supplierList?.reduce((acc, supplierListItem) => {
				if (supplierListItem.is_onboarding === isOnboardingRfx) {
					acc.push({
						value: supplierListItem.id,
						label: supplierListItem.name,
						isCompanyApproved:
							supplierListItem.is_company_approved_list === 1 ||
							supplierListItem.is_company_approved_list === true,
						suffix: (
							<SupplierListSuffix
								listType={getSupplierListType(supplierListItem)}
							/>
						),
					});
				}

				return acc;
			}, []) || [];
		return options;
	};

	const getCurrentSupplierListValue = () => {
		const currentValue = rfqData?.supplierList?.id;
		return currentValue;
	};

	const handleSupplierListChange = (selectedOption) => {
		if (
			!selectedOption?.value ||
			selectedOption.value === rfqData?.supplierList?.id
		) {
			return;
		}

		updateSupplierListMutation.mutate({
			rfqId: rfqData.id,
			supplierListId: selectedOption.value,
		});
	};

	const handleSupplierAttached = async () => {
		try {
			// Refetch the latest data
			const response = await refetch();
			const latestRfqData = response.data;

			if (latestRfqData?.supplierList?.suppliers) {
				const newSuppliers = latestRfqData.supplierList.suppliers;

				// update local state
				setLocalSupplierLists(newSuppliers);

				// Update Formik state
				const newSupplierIds = newSuppliers.map((supplier) => supplier.id);
				setInvitedSuppliers(newSupplierIds);
				formik.setFieldValue("suppliers", newSupplierIds);
			}
		} catch (error) {
			console.error("Error in handleSupplierAttached:", error);
		}
	};

	const ErrorMessageComp = ({ text }) => {
		return <div className="text-md mt-2 text-red-500">{text}</div>;
	};

	if (isLoadingRFQ) {
		return <LoadingSpinner />;
	}

	if (!rfqData) {
		return null;
	}

	return (
		<div style={{ width: "100%" }}>
			<RFQSupplierStyled>
				<div id="layout-dashboard">
					<div className="layout-jumbotron mt-4 flex-wrap gap-x-2 gap-y-3 py-8">
						<div className="layout-jumbotron-1">
							<Heading
								fontSize={"36px"}
								color="#003CE9"
							>
								RFX PARTICIPANTS
							</Heading>
							<div
								id="subheader-page-subtitle"
								className="mt-1 font-roboto text-sm text-tertiary-600"
							>
								Confirm your Rfx participants and invite guests (non-members) to
								this Rfx below.
							</div>
							{showErrorMessage && (
								<ErrorMessageComp text="Please ensure you've added at least one supplier"></ErrorMessageComp>
							)}
						</div>
						<div className="flex-end flex gap-4">
							<div className="min-w-[350px]">
								<Input
									key={`supplier-list-${getCurrentSupplierListValue()}`}
									label="Please Select a Supplier list"
									type="select"
									options={getSupplierListOptionsByProject()}
									value={getCurrentSupplierListValue()}
									onChange={handleSupplierListChange}
									checkIcon={true}
									isSearchable={false}
									placeholder="Select a supplier list"
									className="min-w-[250px]"
									isDisabled={updateSupplierListMutation.isPending}
								/>
							</div>
							<ButtonViewGroup
								setView={setView}
								view={view}
								className="pt-2"
							/>
						</div>
					</div>
					<div className="relative mb-4">
						{view === "grid" ? (
							<GridViewSupplier
								supplierLists={localSupplierLists}
								invitedSuppliers={invitedSuppliers}
								handleInviteSupplier={handleInviteSupplier}
								handleRemoveSupplier={handleRemoveSupplier}
								isLoading={updateSupplierListMutation.isPending}
							/>
						) : (
							<ListViewSupplier
								supplierLists={localSupplierLists}
								invitedSuppliers={invitedSuppliers}
								handleInviteSupplier={handleInviteSupplier}
								handleRemoveSupplier={handleRemoveSupplier}
								isLoading={updateSupplierListMutation.isPending}
							/>
						)}
					</div>
					<InviteForm
						supplierListUuid={supplierListUuid}
						onSupplierAttached={handleSupplierAttached}
					/>
				</div>
			</RFQSupplierStyled>
		</div>
	);
}

export default Suppliers;
